import {
  GatewayControleQuinquennal,
  GatewayTicketNote,
} from "@conformite/gateway";
import { userSession } from "@src/session/UserSession";

export class ControleQuinquennalApi {
  static async getControlesQuinquennaux(): Promise<
    GatewayControleQuinquennal.ControleQuinquennalWithCompletion[]
  > {
    const { data } = await userSession.axiosInstance.get<
      GatewayControleQuinquennal.ControleQuinquennalWithCompletion[]
    >(`/controle-quinquennal`);

    return data.map((controle) => ({
      ...controle,
      dateDepot: controle.dateDepot ? new Date(controle.dateDepot) : null,
    }));
  }

  static async getControleQuinquennal(
    idControleQuinquennal: string
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalWithAxes> {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalWithAxes>(
        `/controle-quinquennal/${idControleQuinquennal}`
      );

    return {
      ...data,
      dateDepot: data.dateDepot ? new Date(data.dateDepot) : null,
    };
  }

  static async getControleQuinquennalAxe(
    idControleQuinquennal: string,
    idAxe: string
  ) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAxeWithFilesAvecTypesAndTotal>(
        `/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}`
      );

    return data;
  }

  static async listControleQuinquennalAxes(
    idControleQuinquennal: string
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles[]> {
    const { data } = await userSession.axiosInstance.get<
      GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles[]
    >(`/controle-quinquennal/${idControleQuinquennal}/axes`);

    return data;
  }

  static async saveControleQuinquennalAxe(
    idControleQuinquennal: string,
    idAxe: string,
    payload: GatewayControleQuinquennal.ControleQuinquennalAxeUpdate,
    files: File[]
  ): Promise<GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append("documents", file);
    });
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        Array.isArray(value) ? JSON.stringify(value) : String(value)
      );
    });
    const response =
      await userSession.axiosInstance.put<GatewayControleQuinquennal.ControleQuinquennalAxeWithFiles>(
        `/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}`,
        formData
      );
    return response.data;
  }

  static async validateDepot(idControleQuinquennal: string): Promise<void> {
    await userSession.axiosInstance.put(
      `/controle-quinquennal/${idControleQuinquennal}/validate`
    );
  }

  static async adminGetControleQuinquennalAxeByEntrepriseId(
    entrepriseId: string
  ) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalAdminResponse>(
        `/admin/controle-quinquennal/${entrepriseId}`
      );
    return data;
  }

  static async createRemediation(
    idControleQuinquennal: string,
    idAxe: string,
    payload: GatewayControleQuinquennal.ControleQuinquenalRemediationRequestType,
    files: File[]
  ) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("documents", file);
    });
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        Array.isArray(value) ? JSON.stringify(value) : String(value)
      );
    });
    const { data } =
      await userSession.axiosInstance.post<GatewayControleQuinquennal.ControleQuinquenalRemediationResponseType>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/remediation`,
        formData
      );
    return data;
  }

  static async updateRemediation(
    idControleQuinquennal: string,
    idAxe: string,
    idRemediation: string,
    payload: GatewayControleQuinquennal.ControleQuinquenalRemediationRequestType,
    files: File[]
  ) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("documents", file);
    });
    Object.entries(payload).forEach(([key, value]) => {
      formData.append(
        key,
        Array.isArray(value) ? JSON.stringify(value) : String(value)
      );
    });
    const { data } =
      await userSession.axiosInstance.put<GatewayControleQuinquennal.ControleQuinquenalRemediationResponseType>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/remediation/${idRemediation}`,
        formData
      );
    return data;
  }

  static async getRemediationByAxeId(
    idControleQuinquennal: string,
    axeId: string
  ) {
    const { data } = await userSession.axiosInstance.get<
      GatewayControleQuinquennal.ControleQuinquennalRemediationWithFileResponseType[]
    >(
      `/controle-quinquennal/${idControleQuinquennal}/axes/${axeId}/remediation`
    );

    return data;
  }

  static async getRapport(idControleQuinquennal: string) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.ControleQuinquennalRapport | null>(
        `/controle-quinquennal/${idControleQuinquennal}/rapport`
      );

    return data;
  }

  static async downloadRapport(idControleQuinquennal: string) {
    const { data } = await userSession.axiosInstance.get<ArrayBuffer>(
      `/controle-quinquennal/${idControleQuinquennal}/rapport/download`,
      { responseType: "blob" }
    );

    return data;
  }

  static async validateAdmin(
    controleQuinquennal: GatewayControleQuinquennal.ControleQuinquennal,
    siren: string,
    file?: File
  ) {
    const formData = new FormData();
    if (file) formData.append("document", file);
    return userSession.axiosInstance.put(
      `/admin/controle-quinquennal/${controleQuinquennal.id}/validate/${siren}`,
      formData
    );
  }

  static async deleteRemediation(remediationId: string) {
    return userSession.axiosInstance.delete(
      `/admin/controle-quinquennal/remediation/${remediationId}`
    );
  }

  static async createTicketNote(
    ticketId: string,
    content: string
  ): Promise<void> {
    await userSession.axiosInstance.post(`/tickets/${ticketId}/notes`, {
      content,
    });
  }

  static async getTicketNotes(ticketId: string) {
    const { data } = await userSession.axiosInstance.get<
      GatewayTicketNote.TicketNote[]
    >(`/tickets/${ticketId}/notes`);
    return data;
  }

  static async getTicketNotesAdmin(ticketId: string) {
    const { data } = await userSession.axiosInstance.get<
      GatewayTicketNote.TicketNote[]
    >(`/admin/tickets/${ticketId}/notes`);
    return data;
  }

  static async getConversationRemediation(
    idControleQuinquennal: string,
    idAxe: string,
    axeName: string
  ) {
    const { data } =
      await userSession.axiosInstance.get<GatewayControleQuinquennal.RemediationConversationResponse>(
        `/admin/controle-quinquennal/${idControleQuinquennal}/axes/${idAxe}/conversations/${axeName}`
      );
    return data.conversations;
  }
}
